import React, { useEffect, useState, useCallback } from 'react';
import { users as usersApi, alertSettings } from '../utils/api';
import { ROLE_LABELS, ROLE_COLORS } from '../utils/auth';
import { useToast, ToastContainer } from '../hooks/useToast';
import { fmtDate } from '../utils/helpers';
import { useT } from '../i18n/I18nContext';

const ROLES = ['admin','buyer','analyst','viewer'];
const CC_LABELS = { producao: '🏭 Produção', marketing: '📣 Marketing' };
const CC_COLORS = { producao: '#2563eb', marketing: '#9333ea' };
const EMPTY_USER = { name:'', email:'', password:'', role:'analyst', cost_center:'', active:true };

export default function Users({ currentUser }) {
  const t = useT();
  const [list, setList]       = useState([]);
  const [alerts, setAlerts]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_USER);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alertForm, setAlertForm]   = useState(null);
  const [saving, setSaving]         = useState(false);
  const [showPwChange, setShowPwChange] = useState(false);
  const [pwForm, setPwForm]         = useState({ current:'', next:'' });
  const { toasts, toast }           = useToast();

  const ROLE_DESC = {
    admin:   t('role.desc.admin'),
    buyer:   t('role.desc.buyer'),
    analyst: t('role.desc.analyst'),
    viewer:  t('role.desc.viewer'),
  };

  const load = useCallback(() => {
    usersApi.list().then(setList);
    alertSettings.get().then(s => { setAlerts(s); setAlertForm(s); });
  }, []);
  useEffect(() => { load(); }, [load]);

  function openNew()    { setForm(EMPTY_USER); setEditing(null); setShowForm(true); }
  function openEdit(u)  { setForm({...u, password:'', cost_center: u.cost_center||'', active: u.active===1||u.active===true}); setEditing(u); setShowForm(true); }
  function sf(k,v)      { setForm(f=>({...f,[k]:v})); }

  async function handleSave() {
    if (!form.name||!form.email) return alert(t('users.validation.name_email'));
    if (!editing && !form.password) return alert(t('users.validation.password'));
    setSaving(true);
    try {
      if (editing) { await usersApi.update(editing.id, form); toast(t('users.saved'),'success'); }
      else         { await usersApi.create(form); toast(t('users.created'),'success'); }
      setShowForm(false); load();
    } catch(e) { toast(e.response?.data?.error||t('common.error'),'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('users.delete.confirm'))) return;
    try { await usersApi.delete(id); toast(t('users.deleted'),'default'); load(); }
    catch(e) { toast(e.response?.data?.error||t('common.error'),'error'); }
  }

  async function saveAlerts() {
    await alertSettings.update(alertForm);
    setAlerts(alertForm); toast(t('users.alerts_saved'),'success'); setShowAlerts(false);
  }

  async function changePassword() {
    if (!pwForm.next||pwForm.next.length<6) return toast(t('users.pw.min_error'),'error');
    try {
      const { auth } = await import('../utils/api');
      await auth.changePassword(pwForm.current, pwForm.next);
      toast(t('users.pw.changed'),'success'); setShowPwChange(false); setPwForm({current:'',next:''});
    } catch(e) { toast(e.response?.data?.error||t('common.error'),'error'); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">👥 {t('users.title')}</div>
          <div className="page-subtitle">{list.length} {t('users.col.name').toLowerCase()}s {t('users.col.registered_at').toLowerCase()}</div>
        </div>
        <div className="gap-8">
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowPwChange(true)}>{t('users.my_password')}</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowAlerts(true)}>{t('users.alert_settings')}</button>
          <button className="btn btn-primary" onClick={openNew}>{t('users.new')}</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:24}}>
        {ROLES.map(role => (
          <div key={role} className="card" style={{borderTop:`3px solid ${ROLE_COLORS[role]}`}}>
            <div className="card-body" style={{padding:'14px 16px'}}>
              <div style={{fontWeight:700,color:ROLE_COLORS[role],fontSize:13,marginBottom:4}}>{ROLE_LABELS[role]}</div>
              <div style={{fontSize:11.5,color:'var(--gray-500)',lineHeight:1.5}}>{ROLE_DESC[role]}</div>
              <div style={{marginTop:8,fontSize:20,fontWeight:700,color:'var(--pantone)',fontFamily:'var(--font-mono)'}}>
                {list.filter(u=>u.role===role).length}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{padding:0}}>
        {list.length===0 ? <div className="empty-state"><div className="empty-icon">👥</div><div className="empty-title">{t('users.empty')}</div></div> : (
          <div className="table-wrap" style={{border:'none',borderRadius:'var(--radius-lg)'}}>
            <table>
              <thead><tr>
                <th>{t('users.col.name')}</th>
                <th>{t('col.email')}</th>
                <th>{t('users.col.profile')}</th>
                <th>{t('users.col.cost_center')}</th>
                <th>{t('users.col.status')}</th>
                <th>{t('users.col.last_login')}</th>
                <th>{t('users.col.registered_at')}</th>
                <th></th>
              </tr></thead>
              <tbody>
                {list.map(u=>(
                  <tr key={u.id}>
                    <td className="bold">{u.name} {u.id===currentUser?.id&&<span style={{fontSize:10,background:'var(--pantone)',color:'white',borderRadius:999,padding:'1px 6px',marginLeft:6}}>{t('users.you')}</span>}</td>
                    <td style={{fontSize:12,color:'var(--gray-500)'}}>{u.email}</td>
                    <td><span style={{padding:'3px 10px',borderRadius:999,fontSize:12,fontWeight:600,background:ROLE_COLORS[u.role]+'22',color:ROLE_COLORS[u.role]}}>{ROLE_LABELS[u.role]}</span></td>
                    <td>{u.cost_center ? <span style={{padding:'3px 10px',borderRadius:999,fontSize:12,fontWeight:600,background:CC_COLORS[u.cost_center]+'22',color:CC_COLORS[u.cost_center]}}>{CC_LABELS[u.cost_center]}</span> : <span style={{fontSize:12,color:'var(--gray-400)'}}>—</span>}</td>
                    <td><span style={{padding:'3px 10px',borderRadius:999,fontSize:12,background:u.active?'var(--green-light)':'var(--red-light)',color:u.active?'var(--green)':'var(--red)',fontWeight:600}}>{u.active?t('users.active'):t('users.inactive')}</span></td>
                    <td className="text-muted">{fmtDate(u.last_login)}</td>
                    <td className="text-muted">{fmtDate(u.created_at)}</td>
                    <td>
                      <div className="gap-8">
                        <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(u)}>✏️</button>
                        {u.id!==currentUser?.id && <button className="btn btn-danger btn-sm" onClick={()=>handleDelete(u.id)}>🗑️</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowForm(false)}>
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-header">
              <div className="modal-title">{editing ? t('users.form.title.edit') : t('users.form.title.new')}</div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{marginBottom:16}}>
                <div className="form-group full"><label>{t('users.form.name')}</label><input value={form.name} onChange={e=>sf('name',e.target.value)} placeholder="João Silva" /></div>
                <div className="form-group full"><label>{t('users.form.email')}</label><input type="email" value={form.email} onChange={e=>sf('email',e.target.value)} placeholder="joao@charth.com" /></div>
                <div className="form-group">
                  <label>{editing ? t('users.form.password_edit') : t('users.form.password_new')}</label>
                  <input type="password" value={form.password} onChange={e=>sf('password',e.target.value)} placeholder={editing ? t('users.form.password_placeholder_edit') : t('users.form.password_placeholder_new')} />
                </div>
                <div className="form-group">
                  <label>{t('users.form.role')}</label>
                  <select value={form.role} onChange={e=>sf('role',e.target.value)}>
                    {ROLES.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                  <span className="form-hint">{ROLE_DESC[form.role]}</span>
                </div>
                <div className="form-group">
                  <label>{t('users.form.cost_center')}</label>
                  <select value={form.cost_center||''} onChange={e=>sf('cost_center',e.target.value||null)}>
                    <option value="">{t('users.form.all_centers')}</option>
                    <option value="producao">🏭 Produção</option>
                    <option value="marketing">📣 Marketing</option>
                  </select>
                  <span className="form-hint">{t('users.form.cost_center_hint')}</span>
                </div>
                {editing && (
                  <div className="form-group">
                    <label>{t('users.form.status')}</label>
                    <select value={form.active?'1':'0'} onChange={e=>sf('active',e.target.value==='1')}>
                      <option value="1">{t('users.active')}</option>
                      <option value="0">{t('users.inactive')}</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowForm(false)}>{t('btn.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'⏳':editing?`💾 ${t('btn.save')}`:t('users.new').replace('+','✅').trim()}</button>
            </div>
          </div>
        </div>
      )}

      {showAlerts && alertForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAlerts(false)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-header">
              <div className="modal-title">{t('users.alert_config.title')}</div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>setShowAlerts(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13,color:'var(--gray-600)',marginBottom:20}}>{t('users.alert_config.desc')}</p>
              <div className="form-grid">
                {[
                  ['ready_days','📋 Pronta p/ Envio (max dias)'],
                  ['sent_days','📤 Enviada (max dias p/ resposta)'],
                  ['waiting_days','📥 Resposta Recebida (max dias p/ análise)'],
                  ['analysis_days','🔍 Em Análise (max dias)'],
                  ['negotiation_days','💬 Em Negociação (max dias)'],
                ].map(([key,label])=>(
                  <div key={key} className="form-group">
                    <label>{label}</label>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input type="number" min={1} max={60} value={alertForm[key]} onChange={e=>setAlertForm(f=>({...f,[key]:parseInt(e.target.value)||1}))} style={{width:80}} />
                      <span style={{fontSize:12,color:'var(--gray-500)'}}>{t('users.alert_config.days')}</span>
                    </div>
                  </div>
                ))}
                <div className="form-group">
                  <label>{t('users.alert_config.multiplier')}</label>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <input type="number" step="0.01" min="1" max="5" value={alertForm.other_costs_multiplier||1.6} onChange={e=>setAlertForm(f=>({...f,other_costs_multiplier:parseFloat(e.target.value)||1.6}))} style={{width:90}} />
                    <span style={{fontSize:12,color:'var(--gray-500)'}}>
                      +{Math.round(((alertForm.other_costs_multiplier||1.6) - 1) * 100)}% {t('users.alert_config.over_fob')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowAlerts(false)}>{t('btn.cancel')}</button>
              <button className="btn btn-primary" onClick={saveAlerts}>💾 {t('btn.save')}</button>
            </div>
          </div>
        </div>
      )}

      {showPwChange && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowPwChange(false)}>
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-header">
              <div className="modal-title">{t('users.pw.title')}</div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>setShowPwChange(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{marginBottom:12}}><label>{t('users.pw.current')}</label><input type="password" value={pwForm.current} onChange={e=>setPwForm(f=>({...f,current:e.target.value}))} /></div>
              <div className="form-group"><label>{t('users.pw.new')}</label><input type="password" value={pwForm.next} onChange={e=>setPwForm(f=>({...f,next:e.target.value}))} placeholder={t('users.pw.min_placeholder')} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowPwChange(false)}>{t('btn.cancel')}</button>
              <button className="btn btn-primary" onClick={changePassword}>{t('users.pw.change_btn')}</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}
